<template>
  <v-dialog :value="value" max-width="980" :fullscreen="isMobile" scrollable @input="$emit('input', $event)">
    <div class="pm">
      <header class="pm__head">
        <span class="pm__icon"><v-icon color="primary">mdi-filter-cog-outline</v-icon></span>
        <div class="pm__titles">
          <div class="pm__title">{{ $t('crmManagePipelines') }}</div>
          <div class="pm__sub">
            {{ $t('crmManagePipelinesHint') }}
            <span v-if="limit !== null" class="pm__usage">{{ $t('crmPipelineUsage', { n: num(pipelines.length), max: num(limit) }) }}</span>
          </div>
        </div>
        <button type="button" class="pm__close" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></button>
      </header>

      <div class="pm__frame">
        <!-- pipelines -->
        <aside v-show="!isMobile || !current" class="pm__side">
          <draggable v-model="pipelines" handle=".pm__grip" :animation="160" ghost-class="pm__ghost" @end="savePipelineOrder">
            <button
              v-for="p in pipelines"
              :key="p.id"
              type="button"
              class="pm__pipe"
              :class="{ on: current && current.id === p.id }"
              @click="select(p)"
            >
              <span class="pm__grip" @click.stop><v-icon small>mdi-drag-vertical</v-icon></span>
              <span class="pm__dot" :style="{ background: p.color || '#2166d1' }" />
              <span class="pm__pipe-main">
                <b>{{ p.title }}</b>
                <small>{{ $t('crmStagesCount', { n: num((p.stages || []).length) }) }}</small>
              </span>
              <v-icon small class="pm__chev">mdi-chevron-left</v-icon>
            </button>
          </draggable>
          <div class="pm__new">
            <v-text-field v-model="newTitle" :label="$t('crmNewPipeline')" outlined dense hide-details :disabled="atLimit" @keydown.enter="createPipeline" />
            <v-btn depressed color="primary" class="pm__sq" :disabled="!newTitle || atLimit" :loading="busy === 'new'" @click="createPipeline"><v-icon small>mdi-plus</v-icon></v-btn>
          </div>
          <div v-if="atLimit" class="pm__limit"><v-icon x-small>mdi-lock-outline</v-icon>{{ $t('crmPipelineLimit') }}</div>
          <div v-else class="pm__hint">{{ $t('crmNewPipelineHint') }}</div>
        </aside>

        <!-- selected pipeline -->
        <section v-if="current" class="pm__main">
          <button v-if="isMobile" type="button" class="pm__back" @click="current = null"><v-icon small>mdi-arrow-right</v-icon>{{ $t('crmPipelines') }}</button>
          <v-alert v-if="error" type="error" text dense class="mb-4">{{ error }}</v-alert>

          <div class="pm__card">
            <div class="pm__card-head">
              <b>{{ $t('crmPipelineInfo') }}</b>
              <v-spacer />
              <v-btn small text :disabled="atLimit" :loading="busy === 'copy'" @click="copyPipeline"><v-icon small class="me-1">mdi-content-copy</v-icon>{{ $t('crmCopy') }}</v-btn>
              <v-btn small text color="error" :loading="busy === 'delpipe'" @click="deletePipeline"><v-icon small class="me-1">mdi-delete-outline</v-icon>{{ $t('crmDelete') }}</v-btn>
            </div>
            <div class="pm__grid">
              <v-text-field v-model="pf.title" :label="$t('crmPipelineName')" outlined dense hide-details />
              <v-text-field v-model="pf.segment" :label="$t('crmPipelineSegment')" outlined dense hide-details />
              <v-textarea v-model="pf.description" :label="$t('crmDescription')" outlined dense hide-details rows="1" auto-grow class="pm__full" />
              <div class="pm__colors pm__full">
                <span>{{ $t('crmColor') }}</span>
                <button v-for="c in palette" :key="c" type="button" class="pm__color" :class="{ on: pf.color === c }" :style="{ background: c }" @click="pf.color = c" />
              </div>
            </div>
            <div class="pm__card-foot">
              <v-btn small depressed color="primary" :disabled="!pipelineDirty" :loading="busy === 'pipe'" @click="savePipeline">{{ $t('crmSave') }}</v-btn>
            </div>
          </div>

          <div class="pm__card">
            <div class="pm__card-head">
              <b>{{ $t('crmStages') }}</b>
              <small class="pm__muted">{{ $t('crmStagesHint') }}</small>
            </div>

            <!-- funnel preview -->
            <div class="pm__preview" :class="{ 'pm__preview--rtl': $vuetify.rtl }">
              <span
                v-for="s in stages"
                :key="s.id"
                class="pm__pv"
                :class="'pm__pv--' + s.kind"
                :style="{ background: s.color || kindColor(s.kind) }"
                :title="s.title"
              >{{ s.title }}</span>
            </div>

            <draggable v-model="stages" handle=".pm__grip" :animation="160" ghost-class="pm__ghost" @end="saveStageOrder">
              <div v-for="s in stages" :key="s.id" class="pm__stage" :class="{ 'pm__stage--edit': editing === s.id }">
                <span class="pm__grip"><v-icon small>mdi-drag-vertical</v-icon></span>
                <template v-if="editing !== s.id">
                  <span class="pm__dot" :style="{ background: s.color || kindColor(s.kind) }" />
                  <div class="pm__stage-main">
                    <b>{{ s.title }}</b>
                    <small>
                      <span class="pm__kind" :class="'pm__kind--' + s.kind">{{ $t('crmKind_' + s.kind) }}</span>
                      <span v-if="s.kind === 'open'">{{ $t('crmWinProb') }} {{ num(s.win_probability || 0) }}٪</span>
                      <span v-if="s.allow_backward === false" class="pm__flag"><v-icon x-small>mdi-arrow-left-bold-outline</v-icon>{{ $t('crmNoBackward') }}</span>
                      <span v-if="hasConditions(s)" class="pm__flag pm__flag--cond"><v-icon x-small>mdi-shield-check-outline</v-icon>{{ $t('crmEntryConditions') }}</span>
                    </small>
                  </div>
                  <button type="button" class="pm__btn" :title="$t('crmEdit')" @click="startEdit(s)"><v-icon small>mdi-pencil-outline</v-icon></button>
                  <button type="button" class="pm__btn pm__btn--del" :title="$t('crmDelete')" @click="deleteStage(s)"><v-icon small>mdi-delete-outline</v-icon></button>
                </template>
                <div v-else class="pm__edit">
                  <div class="pm__grid">
                    <v-text-field v-model="sf.title" :label="$t('crmStageName')" outlined dense hide-details autofocus />
                    <v-select v-model="sf.kind" :items="kindItems" :label="$t('crmStageKind')" outlined dense hide-details :hint="$t('crmKindLockedHint')" />
                    <div v-if="sf.kind === 'open'" class="pm__slider pm__full">
                      <span>{{ $t('crmWinProb') }}</span>
                      <v-slider v-model="sf.win_probability" min="0" max="100" step="5" thumb-label hide-details />
                      <b>{{ num(sf.win_probability) }}٪</b>
                    </div>
                    <div class="pm__colors pm__full">
                      <span>{{ $t('crmColor') }}</span>
                      <button v-for="c in palette" :key="c" type="button" class="pm__color" :class="{ on: sf.color === c }" :style="{ background: c }" @click="sf.color = c" />
                    </div>
                    <v-switch v-model="sf.allow_backward" inset dense hide-details :label="$t('crmAllowBackward')" class="pm__full" />
                  </div>

                  <!-- entry conditions (doc §5 / §6.3) -->
                  <div class="pm__cond">
                    <div class="pm__cond-title"><v-icon x-small>mdi-shield-check-outline</v-icon>{{ $t('crmEntryConditions') }}</div>
                    <div class="pm__cond-sub">{{ $t('crmEntryConditionsHint') }}</div>
                    <label v-for="c in conditionPresets" :key="c.key" class="pm__check">
                      <v-simple-checkbox :value="sf.cond.includes(c.key)" :ripple="false" @input="toggleCond(c.key)" />
                      <span>{{ c.label }}</span>
                      <v-text-field
                        v-if="c.amount && sf.cond.includes(c.key)"
                        v-model.number="sf.minAmount"
                        type="number"
                        dense
                        outlined
                        hide-details
                        dir="ltr"
                        class="pm__amount"
                      />
                    </label>
                    <v-text-field v-if="sf.cond.length" v-model="sf.condMessage" :label="$t('crmConditionMessage')" outlined dense hide-details class="mt-3" />
                    <div v-if="sf.extraRules.length" class="pm__cond-sub mt-2"><v-icon x-small>mdi-information-outline</v-icon>{{ $t('crmConditionsKept', { n: num(sf.extraRules.length) }) }}</div>
                  </div>

                  <div class="pm__edit-foot">
                    <v-btn small depressed color="primary" :loading="busy === 'stage'" @click="saveStage(s)">{{ $t('crmSave') }}</v-btn>
                    <v-btn small text @click="editing = null">{{ $t('crmCancel') }}</v-btn>
                  </div>
                </div>
              </div>
            </draggable>

            <div class="pm__addstage">
              <v-text-field v-model="newStage.title" :label="$t('crmNewStage')" outlined dense hide-details @keydown.enter="createStage" />
              <v-select v-model="newStage.kind" :items="kindItems" outlined dense hide-details class="pm__kindsel" />
              <v-btn depressed color="primary" class="pm__sq" :disabled="!newStage.title" :loading="busy === 'newstage'" @click="createStage"><v-icon small>mdi-plus</v-icon></v-btn>
            </div>
          </div>
        </section>
        <section v-else-if="!isMobile" class="pm__main pm__main--empty">
          <v-icon size="40" color="primary">mdi-filter-outline</v-icon>
          <div>{{ $t('crmPickPipeline') }}</div>
        </section>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import draggable from 'vuedraggable'
import api, { crmError } from '../crmApi'
import { crm, isFa, reloadPipelines } from '../crmStore'

const PALETTE = ['#2166d1', '#00838f', '#0f9d76', '#6a4fd1', '#8b5cf6', '#d9822b', '#e0a030', '#c0392b', '#8a94a6']
const KIND_COLORS = { open: '#2166d1', won: '#0f9d76', lost: '#c0392b' }

export default {
  name: 'CrmPipelineManager',
  components: { draggable },
  inject: ['shell'],
  props: { value: Boolean, initialId: { type: Number, default: null } },
  data: () => ({
    pipelines: [],
    current: null,
    pf: {},
    stages: [],
    editing: null,
    sf: {},
    newTitle: '',
    newStage: { title: '', kind: 'open' },
    busy: '',
    error: ''
  }),
  computed: {
    palette () { return PALETTE },
    limit () {
      const l = crm.me && crm.me.limits
      return l && l.max_pipelines !== null && l.max_pipelines !== undefined ? l.max_pipelines : null
    },
    atLimit () { return this.limit !== null && this.pipelines.length >= this.limit },
    kindItems () { return ['open', 'won', 'lost'].map(k => ({ value: k, text: this.$t('crmKind_' + k) })) },
    pipelineDirty () {
      const c = this.current
      if (!c) return false
      return ['title', 'color', 'description', 'segment'].some(k => (this.pf[k] || '') !== (c[k] || ''))
    },
    conditionPresets () {
      return [
        { key: 'products', label: this.$t('crmCondProducts'), rule: { field: 'products_count', op: '>', value: 0 } },
        { key: 'close', label: this.$t('crmCondCloseDate'), rule: { field: 'expected_close_date', op: 'not_empty' } },
        { key: 'customer', label: this.$t('crmCondCustomer'), rule: { field: 'person_id', op: 'not_empty' } },
        { key: 'activity', label: this.$t('crmCondActivity'), rule: { field: 'activities_count', op: '>', value: 0 } },
        { key: 'amount', label: this.$t('crmCondAmount'), amount: true, rule: { field: 'amount', op: '>=' } }
      ]
    }
  },
  watch: {
    value (v) {
      if (!v) return
      this.sync()
      const first = this.pipelines.find(p => p.id === this.initialId) || (this.isMobile ? null : this.pipelines[0])
      if (first) this.select(first)
      else this.current = null
    }
  },
  methods: {
    num (v) { return Number(v || 0).toLocaleString(isFa() ? 'fa-IR' : 'en-US') },
    kindColor (k) { return KIND_COLORS[k] || '#8a94a6' },
    sync () {
      this.pipelines = crm.pipelines.slice()
      if (this.current) {
        const fresh = this.pipelines.find(p => p.id === this.current.id)
        if (fresh) this.select(fresh, true)
        else this.current = null
      }
    },
    select (p, keepEdit = false) {
      this.current = p
      this.pf = { title: p.title || '', color: p.color || PALETTE[0], description: p.description || '', segment: p.segment || '' }
      this.stages = (p.stages || []).slice().sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      if (!keepEdit) this.editing = null
      this.error = ''
    },
    async refresh () {
      await reloadPipelines()
      this.sync()
      this.shell.$emit('changed', { type: 'deal', record: null })
    },
    async run (tag, fn) {
      this.busy = tag
      this.error = ''
      try {
        const res = await fn()
        await this.refresh()
        return res
      } catch (e) {
        this.error = crmError(e).message
        this.shell.toast(this.error, { error: true })
      } finally {
        this.busy = ''
      }
    },
    // ---- pipelines ----
    async createPipeline () {
      if (!this.newTitle || this.atLimit) return
      const title = this.newTitle
      const res = await this.run('new', () => api.createPipeline({ title, color: PALETTE[this.pipelines.length % PALETTE.length] }))
      this.newTitle = ''
      const created = res && (res.pipeline || res)
      const p = created && this.pipelines.find(x => x.id === created.id)
      if (p) this.select(p)
    },
    savePipeline () { return this.run('pipe', () => api.updatePipeline(this.current.id, this.pf)) },
    savePipelineOrder () { return this.run('order', () => api.reorderPipelines(this.pipelines.map(p => p.id))) },
    async copyPipeline () {
      const title = await this.shell.ask(this.$t('crmCopyPipeline'), this.$t('crmPipelineName'), this.current.title + ' — ' + this.$t('crmCopySuffix'))
      if (!title) return
      const res = await this.run('copy', () => api.copyPipeline(this.current.id, { title, with_forms: true }))
      const created = res && (res.pipeline || res)
      const p = created && this.pipelines.find(x => x.id === created.id)
      if (p) this.select(p)
    },
    async deletePipeline () {
      const name = this.current.title
      const typed = await this.shell.ask(this.$t('crmConfirmDelete', { name }), this.$t('crmTypeToConfirm'))
      if (!typed || typed.trim() !== String(name).trim()) {
        if (typed) this.shell.toast(this.$t('crmConfirmMismatch'), { error: true })
        return
      }
      const id = this.current.id
      this.current = null
      await this.run('delpipe', () => api.deletePipeline(id))
    },
    // ---- stages ----
    hasConditions (s) {
      const c = s.entry_conditions
      return !!(c && Array.isArray(c.rules) && c.rules.length)
    },
    startEdit (s) {
      const rules = (s.entry_conditions && s.entry_conditions.rules) || []
      const cond = []
      const extra = []
      let minAmount = null
      rules.forEach(r => {
        const preset = this.conditionPresets.find(p => p.rule.field === r.field && p.rule.op === r.op)
        if (preset) {
          cond.push(preset.key)
          if (preset.amount) minAmount = r.value
        } else extra.push(r)
      })
      this.editing = s.id
      this.sf = {
        title: s.title,
        kind: s.kind || 'open',
        win_probability: s.win_probability || 0,
        color: s.color || this.kindColor(s.kind),
        allow_backward: s.allow_backward !== false,
        cond,
        minAmount,
        condMessage: (s.entry_conditions && s.entry_conditions.message) || '',
        extraRules: extra
      }
    },
    toggleCond (k) {
      this.sf.cond = this.sf.cond.includes(k) ? this.sf.cond.filter(x => x !== k) : this.sf.cond.concat(k)
    },
    buildConditions () {
      const rules = this.sf.cond.map(k => {
        const p = this.conditionPresets.find(x => x.key === k)
        return p.amount ? { ...p.rule, value: Number(this.sf.minAmount) || 0 } : { ...p.rule }
      }).concat(this.sf.extraRules)
      if (!rules.length) return null
      const c = { boolean: 'AND', rules }
      if (this.sf.condMessage) c.message = this.sf.condMessage
      return c
    },
    saveStage (s) {
      const body = {
        title: this.sf.title,
        color: this.sf.color,
        kind: this.sf.kind,
        win_probability: this.sf.kind === 'open' ? this.sf.win_probability : (this.sf.kind === 'won' ? 100 : 0),
        allow_backward: this.sf.allow_backward,
        entry_conditions: this.buildConditions()
      }
      return this.run('stage', async () => {
        await api.updateStage(s.id, body)
        this.editing = null
      })
    },
    saveStageOrder () { return this.run('order', () => api.reorderStages(this.current.id, this.stages.map(s => s.id))) },
    createStage () {
      if (!this.newStage.title) return
      const kind = this.newStage.kind
      const body = { title: this.newStage.title, kind, color: this.kindColor(kind), win_probability: kind === 'won' ? 100 : (kind === 'lost' ? 0 : 50), allow_backward: true }
      return this.run('newstage', async () => {
        await api.createStage(this.current.id, body)
        this.newStage = { title: '', kind: 'open' }
      })
    },
    async deleteStage (s) {
      const others = this.stages.filter(x => x.id !== s.id)
      if (s.kind === 'open' && !others.some(x => x.kind === 'open')) {
        this.shell.toast(this.$t('crmNeedOpenStage'), { error: true })
        return
      }
      const target = others.find(x => x.kind === s.kind) || others[0]
      const typed = await this.shell.ask(this.$t('crmDeleteStageConfirm', { name: s.title, to: target ? target.title : '—' }), this.$t('crmTypeToConfirm'))
      if (!typed || typed.trim() !== String(s.title).trim()) {
        if (typed) this.shell.toast(this.$t('crmConfirmMismatch'), { error: true })
        return
      }
      return this.run('delstage', () => api.deleteStage(s.id, target && target.id))
    }
  }
}
</script>

<style scoped>
.pm { background: #fff; border-radius: 6px; overflow: hidden; display: flex; flex-direction: column; height: 86vh; }
.theme--dark .pm { background: #1e1e1e; }
.pm__head { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid rgba(15, 23, 42, .07); flex-shrink: 0; }
.pm__icon { width: 40px; height: 40px; border-radius: 6px; background: rgba(25, 118, 210, .1); display: flex; align-items: center; justify-content: center; }
.pm__titles { flex: 1; min-width: 0; }
.pm__title { font-weight: 700; }
.pm__sub { font-size: .76rem; opacity: .7; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.pm__usage { padding: 1px 8px; border-radius: 6px; background: rgba(25, 118, 210, .1); color: var(--v-primary-base); font-weight: 600; }
.pm__close { width: 36px; height: 36px; border-radius: 6px; background: rgba(0, 0, 0, .05); display: flex; align-items: center; justify-content: center; }
.pm__frame { flex: 1; min-height: 0; display: flex; }
.pm__side { width: 280px; flex-shrink: 0; border-inline-end: 1px solid rgba(15, 23, 42, .07); padding: 12px; overflow-y: auto; display: flex; flex-direction: column; gap: 4px; background: #fafbfd; }
.theme--dark .pm__side { background: #232323; }
.pm__pipe { display: flex; align-items: center; gap: 8px; width: 100%; padding: 9px 8px; border-radius: 6px; text-align: start; margin-bottom: 4px; transition: background .15s; }
.pm__pipe:hover { background: rgba(127, 127, 127, .08); }
.pm__pipe.on { background: rgba(25, 118, 210, .1); color: var(--v-primary-base); }
.pm__pipe-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.pm__pipe-main b { font-size: .85rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pm__pipe-main small { font-size: .7rem; opacity: .65; }
.pm__chev { opacity: .4; }
.pm__grip { cursor: grab; opacity: .45; display: inline-flex; }
.pm__dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.pm__ghost { opacity: .4; }
.pm__new { display: flex; gap: 6px; align-items: center; margin-top: 10px; }
.pm__sq { min-width: 40px !important; width: 40px; height: 40px !important; border-radius: 6px !important; padding: 0 !important; }
.pm__limit, .pm__hint { font-size: .72rem; opacity: .65; display: flex; align-items: center; gap: 4px; padding: 6px 2px; }
.pm__limit { color: #b86a1a; opacity: 1; }
.pm__main { flex: 1; min-width: 0; overflow-y: auto; padding: 16px 18px 24px; }
.pm__main--empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; opacity: .7; }
.pm__back { display: inline-flex; align-items: center; gap: 4px; font-size: .82rem; font-weight: 600; margin-bottom: 12px; color: var(--v-primary-base); }
.pm__card { border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; padding: 14px; margin-bottom: 14px; }
.pm__card-head { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; font-size: .88rem; flex-wrap: wrap; }
.pm__card-foot { display: flex; justify-content: flex-end; margin-top: 14px; }
.pm__muted { opacity: .6; font-size: .74rem; }
.pm__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 12px; }
.pm__full { grid-column: 1 / -1; }
.pm__colors { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: .78rem; }
.pm__colors span { opacity: .7; margin-inline-end: 6px; }
.pm__color { width: 22px; height: 22px; border-radius: 50%; border: 2px solid transparent; box-shadow: 0 0 0 1px rgba(15, 23, 42, .1); }
.pm__color.on { border-color: #fff; box-shadow: 0 0 0 2px var(--v-primary-base); }
.pm__preview { display: flex; gap: 2px; margin-bottom: 14px; border-radius: 6px; overflow: hidden; }
/* chevrons point in reading direction: right in LTR, left in RTL; the first one has a flat start edge */
.pm__pv { flex: 1; min-width: 0; padding: 6px 12px; font-size: .68rem; font-weight: 700; color: #fff; text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%); }
.pm__pv:first-child { clip-path: polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%); }
.pm__preview--rtl .pm__pv { clip-path: polygon(8px 0, 100% 0, calc(100% - 8px) 50%, 100% 100%, 8px 100%, 0 50%); }
.pm__preview--rtl .pm__pv:first-child { clip-path: polygon(8px 0, 100% 0, 100% 100%, 8px 100%, 0 50%); }
.pm__preview .pm__pv--won, .pm__preview .pm__pv--lost { flex: 0 0 auto; padding: 6px 14px; clip-path: none; border-radius: 6px; margin-inline-start: 4px; }
.pm__stage { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 6px; margin-bottom: 6px; background: #fff; }
.theme--dark .pm__stage { background: #262626; }
.pm__stage--edit { align-items: flex-start; border-color: var(--v-primary-base); }
.pm__stage-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pm__stage-main b { font-size: .86rem; }
.pm__stage-main small { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: .72rem; opacity: .8; }
.pm__kind { padding: 0 7px; border-radius: 6px; font-weight: 700; background: rgba(33, 102, 209, .1); color: #2166d1; }
.pm__kind--won { background: rgba(15, 157, 118, .12); color: #0f9d76; }
.pm__kind--lost { background: rgba(192, 57, 43, .1); color: #c0392b; }
.pm__flag { display: inline-flex; align-items: center; gap: 3px; padding: 0 6px; border-radius: 6px; background: rgba(127, 127, 127, .12); }
.pm__flag--cond { background: rgba(106, 79, 209, .1); color: #6a4fd1; }
.pm__flag--cond ::v-deep .v-icon { color: #6a4fd1 !important; }
.pm__btn { width: 30px; height: 30px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
.pm__btn:hover { background: rgba(127, 127, 127, .12); }
.pm__btn--del:hover ::v-deep .v-icon { color: #c0392b !important; }
.pm__edit { flex: 1; display: flex; flex-direction: column; gap: 14px; padding: 4px 0; min-width: 0; }
.pm__edit-foot { display: flex; gap: 8px; }
.pm__slider { display: flex; align-items: center; gap: 12px; font-size: .8rem; }
.pm__slider span { min-width: 90px; opacity: .7; }
.pm__slider b { min-width: 40px; text-align: end; }
.pm__cond { border-radius: 6px; background: rgba(106, 79, 209, .05); border: 1px solid rgba(106, 79, 209, .18); padding: 12px; }
.pm__cond-title { display: flex; align-items: center; gap: 5px; font-weight: 700; font-size: .82rem; }
.pm__cond-sub { font-size: .72rem; opacity: .7; margin: 2px 0 8px; display: flex; align-items: center; gap: 4px; }
.pm__check { display: flex; align-items: center; gap: 6px; padding: 3px 0; font-size: .8rem; cursor: pointer; }
.pm__check ::v-deep .v-simple-checkbox { margin: 0; }
.pm__amount { max-width: 160px; margin-inline-start: 8px !important; }
.pm__addstage { display: flex; gap: 8px; align-items: center; margin-top: 10px; padding-top: 10px; border-top: 1px dashed rgba(15, 23, 42, .14); }
.pm__kindsel { max-width: 150px; }
.pm ::v-deep .v-input { height: auto !important; margin: 0; padding: 0; }
.pm ::v-deep fieldset { border-radius: 6px; }
@media (max-width: 799px) {
  .pm { height: 100%; border-radius: 0; }
  .pm__side { width: 100%; border: 0; background: transparent; }
  .pm__grid { grid-template-columns: 1fr; }
  .pm__addstage { flex-wrap: wrap; }
  .pm__kindsel { max-width: none; flex: 1; }
}
</style>
